import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ThemeToggleComponent } from '@components/partials/theme-toggle/theme-toggle.component';
import { AuthService } from '@services/auth.service';
import { filter } from 'rxjs';

const MOBILE_QUERY = '(max-width: 860px)';
const SCROLL_LOCK_CLASS = 'has-open-drawer';

@Component({
  selector: 'app-private',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, ThemeToggleComponent],
  templateUrl: './private.layout.html',
  styleUrl: './private.layout.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class PrivateLayout {
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly user = this.authService.currentUser;

  private readonly menuToggle = viewChild<ElementRef<HTMLButtonElement>>('menuToggle');
  private readonly sidebar = viewChild<ElementRef<HTMLElement>>('sidebar');

  readonly mobileMenuOpen = signal(false);
  readonly isMobileViewport = signal(matchesMobile());
  readonly pageTitle = signal(this.resolvePageTitle(this.router.url));

  readonly sidebarHidden = computed(() => this.isMobileViewport() && !this.mobileMenuOpen());
  readonly contentHidden = computed(() => this.isMobileViewport() && this.mobileMenuOpen());

  readonly userName = computed(() => {
    const name = `${this.user()?.firstName ?? ''} ${this.user()?.lastName ?? ''}`.trim();
    return name || 'Account user';
  });
  readonly userInitials = computed(() => {
    const first = this.user()?.firstName?.trim()?.[0] ?? '';
    const last = this.user()?.lastName?.trim()?.[0] ?? '';
    return `${first}${last}`.toUpperCase() || 'S';
  });
  readonly userEmail = computed(() => this.user()?.emailAddress || 'Savant account');
  readonly todayLabel = computed(() =>
    new Intl.DateTimeFormat('en', { weekday: 'short', month: 'short', day: 'numeric' }).format(new Date()),
  );

  constructor() {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((event) => this.pageTitle.set(this.resolvePageTitle(event.urlAfterRedirects)));

    this.watchViewport();

    effect(() => {
      const open = this.contentHidden();
      document.body.classList.toggle(SCROLL_LOCK_CLASS, open);
    });

    effect(() => {
      if (!this.isMobileViewport()) {
        this.mobileMenuOpen.set(false);
      }
    });

    this.destroyRef.onDestroy(() => document.body.classList.remove(SCROLL_LOCK_CLASS));
  }

  toggleMobileMenu(): void {
    this.mobileMenuOpen.update((open) => !open);
    if (this.mobileMenuOpen()) {
      this.focusDrawer();
    } else {
      this.restoreFocus();
    }
  }

  closeMobileMenu(): void {
    if (!this.mobileMenuOpen()) {
      return;
    }
    this.mobileMenuOpen.set(false);
    this.restoreFocus();
  }

  logout(): void {
    this.authService.logout();
    void this.router.navigateByUrl('/auth/login');
  }

  protected onEscape(): void {
    this.closeMobileMenu();
  }

  private focusDrawer(): void {
    this.afterRender(() => {
      const drawer = this.sidebar()?.nativeElement;
      drawer?.querySelector<HTMLElement>('a, button')?.focus();
    });
  }

  private restoreFocus(): void {
    this.afterRender(() => this.menuToggle()?.nativeElement.focus());
  }

  private afterRender(action: () => void): void {
    afterNextRender(action, { injector: this.injector });
  }

  private watchViewport(): void {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const query = window.matchMedia(MOBILE_QUERY);
    const update = (event: MediaQueryListEvent) => this.isMobileViewport.set(event.matches);
    query.addEventListener('change', update);
    this.destroyRef.onDestroy(() => query.removeEventListener('change', update));
  }

  private resolvePageTitle(url: string): string {
    const path = url.split(/[?#]/, 1)[0].replace(/\/+$/, '');
    if (path.endsWith('/profile')) return 'Your profile';
    if (path.includes('/partners')) return 'Partners';
    if (path.endsWith('/users')) return 'Users';
    if (path.endsWith('/statements')) return 'Account statements';
    if (path.endsWith('/help')) return 'Help & support';
    if (path.includes('/accounts/')) return 'Account details';
    return 'Overview';
  }
}

function matchesMobile(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return false;
  }
  return window.matchMedia(MOBILE_QUERY).matches;
}
